import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';

import { includes } from 'lodash';
import ProductApi from '../../../lib/api/product/ProductApi';
import { setRequestForm } from '../../../lib/common';
import { setMinimumPrice2000WonCategoryIdList, setProductPayMethodTypeInfo } from '../../../lib/utils/productUtils';
import { setChoiceOption } from '../../../lib/utils';
import { PRODUCT_OPTION_ITEM_MODEL, PRODUCT_STOCK_AND_OPTION_MODEL } from '../../../store/modules/product/models';
import { PRODUCT } from '../../../constants/queryKeys';
import {
  DEFAULT_DIRECT_DELIVERY_TIME_LIMIT,
  DEFAULT_ORDER_MADE_DATE,
  ENABLE_CHANGE_PRODUCT_STATUS_LIST,
  FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST,
  MINIMUM_PRICE_9900_WON_CATEGORY_PRICE,
  PRODUCT_MANAGEMENT_TYPE,
  PRODUCT_MINIMUM_PRICE,
  PRODUCT_STATUS,
} from '../../../constants/service/app/productConstants';
import { COMMON_RES_CODE } from '../../../constants/responseCode/commonResCode';

const queryFn = async (productId, initialData) => {
  const result = await ProductApi.getProductDetail(setRequestForm({ productId }, true));

  if (result?.status !== COMMON_RES_CODE.SUCCESS) {
    throw result.msg;
  }

  return produce(initialData, (draft) => {
    /**
     *  상품 1차 카테고리 리스트
     */
    draft.mainProductCategorySelectList = result.data.mainProductCategorySelectList;

    /**
     *  상품 2차 카테고리 리스트
     */
    draft.subProductCategorySelectList = result.data.subProductCategorySelectList;

    /**
     *  판매가가 2,000원 이상만 올릴 수 있는 1차 카테고리 id 리스트
     */
    draft.minimumPrice2000WonCategoryIdList = setMinimumPrice2000WonCategoryIdList(
      result.data.mainProductCategorySelectList.normal,
    );

    /**
     *  상품 속성
     */
    draft.productPropertyList = result.data.productPropertyList;

    /**
     *  상품 태그 그룹별 태그
     */
    draft.productTagInfo = result.data.productTagInfo;

    /**
     *  상품 결제 타입에 해당되는 결제수단 정보
     */
    draft.productPayMethodTypeInfo = setProductPayMethodTypeInfo(result.data.productPayMethodTypeAllList);

    const { brandFeeInfoList } = result.data.productDetail.proStockAndOption;
    const isFulfillmentProduct = brandFeeInfoList.find((item) =>
      FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST.includes(item.productManagementType),
    );

    /**
     *  default 수수료 정보
     */
    const targetFeeInfo = brandFeeInfoList.find((item) => PRODUCT_MANAGEMENT_TYPE.ENTER === item.productManagementType);

    /**
     *  상품 상세
     */
    draft.productDetail = {
      ...result.data.productDetail,
      productUpdateLogId: result.data.productUpdateLogId,
      proStockAndOption: {
        ...draft.productDetail.proStockAndOption,
        ...result.data.productDetail.proStockAndOption,
        number: '',
        resetOptionAvailable: true,
        optionList: result.data.productDetail.proStockAndOption.optionList.length
          ? result.data.productDetail.proStockAndOption.optionList.map((item) => ({
              ...PRODUCT_OPTION_ITEM_MODEL,
              ...item,
              isNew: true,
              productManagementTypeSelectList: isFulfillmentProduct
                ? PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList.map((subItem) => ({
                    ...subItem,
                    disabled: false,
                  }))
                : PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList,
            }))
          : [],
        feeInfo: {
          ...draft.productDetail.proStockAndOption.feeInfo,
          ...targetFeeInfo,
          ...result.data.productDetail.proStockAndOption.feeInfo,
          productManagementTypeSelectList: isFulfillmentProduct
            ? draft.productDetail.proStockAndOption.feeInfo.productManagementTypeSelectList.map((subItem) => ({
                ...subItem,
                disabled: false,
              }))
            : draft.productDetail.proStockAndOption.feeInfo.productManagementTypeSelectList,
        },
      },
    };

    /**
     * 상품 속성
     * 검수 요청 속성, 승인 거절된 속성 null 처리
     */
    const { productPropertyList } = result.data.productDetail.proInfo;
    for (const key in productPropertyList) {
      draft.productDetail.proInfo.productPropertyList[key] = {
        ...productPropertyList[key],
        list: {
          ...productPropertyList[key].list,
          confirmReady: null,
          reject: null,
        },
      };
    }

    /**
     *  상품 상태를 승인대기로 변경
     */
    draft.productDetail.proStatusAndBrandInfo.status = PRODUCT_STATUS.CONFIRM_READY;

    /**
     *  상품 최소값
     *  [카테고리에 따라서 상품의 최소값이 변경됨]
     */
    draft.productDetail.proCategory.minimumPrice = includes(
      draft.minimumPrice2000WonCategoryIdList,
      draft.productDetail.proCategory.categoryList[0]?.mainCategoryId,
    )
      ? PRODUCT_MINIMUM_PRICE
      : MINIMUM_PRICE_9900_WON_CATEGORY_PRICE;

    /**
     * 카테고리
     */
    draft.productDetail.proCategory.categoryList = draft.productDetail.proCategory.categoryList.map((item) => {
      const main =
        result.data.mainProductCategorySelectList.normal.find((category) => category.id === item.mainCategoryId) ||
        result.data.mainProductCategorySelectList.emphasis.find((category) => category.id === item.mainCategoryId);
      const subCategory = item.subCategoryId.map((id) => {
        const sub = result.data.subProductCategorySelectList[item.mainCategoryId].find(
          (category) => category.id === id,
        );
        return {
          id: sub.id,
          name: sub.name,
          productCategoryUpdateLogId: sub.productCategoryUpdateLogId,
        };
      });
      return {
        id: main.id,
        name: main.name,
        productCategoryUpdateLogId: main.productCategoryUpdateLogId,
        subCategory,
      };
    });

    /**
     *  상품 상태가 삭제일 경우 isDelete = true 으로 업데이트
     */
    draft.productDetail.proStatusAndBrandInfo.isDelete =
      result.data.productDetail.proStatusAndBrandInfo.status === PRODUCT_STATUS.DELETE;

    /**
     *  상품 버전 개수
     */
    draft.productDetail.proStatusAndBrandInfo.versionCnt = result.data.versionCnt;

    /**
     *  옵션 리스트가 있다면 choiceOption 에 값 설정해주기
     */
    if (result.data.productDetail.proStockAndOption.isOption) {
      draft.productDetail.proStockAndOption.choiceOption = setChoiceOption(result.data.productDetail.proStockAndOption);
    } else draft.productDetail.proStockAndOption.choiceOption = PRODUCT_STOCK_AND_OPTION_MODEL.choiceOption;

    /**
     *  상품 이미지에 isEdit = false 속성을 추가해준다. 이미지가 변경이 되면 isEdit = true 로 변경한다.
     *  대표 이미지
     */
    draft.productDetail.proImg.mainImg = draft.productDetail.proImg.mainImg.map((item) => {
      return { ...item, isEdit: false };
    });
    /**
     *  추가 이미지
     */
    draft.productDetail.proImg.subImg = draft.productDetail.proImg.subImg.map((item) => {
      return { ...item, isEdit: false };
    });
    /**
     *  상세 이미지
     */
    draft.productDetail.proDescriptionImg.detailImg = draft.productDetail.proDescriptionImg.detailImg.map((item) => {
      return { ...item, isEdit: false };
    });

    /**
     *  상품 상태가 2(판매중), 3(품절), 7(판매 중지)인 경우에만 상태를 변경할 수 있도록 함
     */
    draft.productDetail.proStatusAndBrandInfo.enableStatus = includes(
      ENABLE_CHANGE_PRODUCT_STATUS_LIST,
      result.data.productDetail.proStatusAndBrandInfo.status,
    );

    /**
     *  주문 시 제작 소요시간이 설정되어 있지 않다면 기본값으로 설정
     */
    if (!draft.productDetail.proDeliveryInfo.orderMadeDate) {
      draft.productDetail.proDeliveryInfo.orderMadeDate = DEFAULT_ORDER_MADE_DATE;
    }

    /**
     *  바로 배송 기준 시간이 설정되어 있지 않다면 기본값으로 설정
     */
    if (!draft.productDetail.proDeliveryInfo.directDeliveryTimeLimit) {
      draft.productDetail.proDeliveryInfo.directDeliveryTimeLimit = DEFAULT_DIRECT_DELIVERY_TIME_LIMIT;
    }
  });
};

const usePrepareInsertProduct = (productId, options) => {
  const query = useQuery([...PRODUCT.PRODUCT_DETAIL], () => queryFn(productId, options.initialData), options);
  const queryClient = useQueryClient();

  useEffect(() => {
    return () => {
      queryClient.removeQueries([...PRODUCT.PRODUCT_DETAIL]);
    };
  }, []);

  return query;
};

export default usePrepareInsertProduct;
